// Inline URL form for adding a weather_file backed by a YouTube video or
// external link (instead of an uploaded file). Rendered by MultiFileUploader
// when the user clicks "Add YouTube" or "Add Link" in the pick step.
//
// STAGE 1 (this file): layout + field collection only. The Add button is
// disabled and labelled "Coming soon" — submission wiring (POST to
// /api/cms/jsonapi/node/weather_file with field_youtube_video_id /
// field_video_url) lands in stage 2.

import React, { useState, useEffect } from 'react';
import type { TaxonomyTerm } from '../../hooks/useTaxonomyTerms';
import { todayInNwt } from '../../utils/nwtDate';

// YouTube-imported weather_file nodes are tagged with this subcategory by the
// cron-driven import (see intellifire_cms/docs/WEATHER_VIDEO_INTEGRATION.txt).
// Pre-select it for manual YouTube adds so editors don't have to pick it every
// time — they can still change it.
const YOUTUBE_DEFAULT_SUBCATEGORY = 'Weather Briefing';

export type WeatherUrlKind = 'youtube' | 'link';

interface Props {
  kind: WeatherUrlKind;
  subcategories: TaxonomyTerm[];
  onCancel: () => void;
  onSuccess: () => void;
}

const WeatherUrlForm: React.FC<Props> = ({ kind, subcategories, onCancel, onSuccess }) => {
  const [url, setUrl] = useState('');
  const [description, setDescription] = useState('');
  const [date, setDate] = useState(todayInNwt());
  const [subcategoryId, setSubcategoryId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isYouTube = kind === 'youtube';
  const canSubmit = url.trim() !== '' && date !== '' && subcategoryId !== '' && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch('/api/file-upload/weather_file/url', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          url: url.trim(),
          kind,
          fileDate: date,
          subcategoryId,
          description: description.trim() || undefined,
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || body.success === false) {
        throw new Error(body.message || body.error || `HTTP ${res.status}`);
      }
      onSuccess();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to add');
      setSubmitting(false);
    }
    // intentionally don't reset submitting on success — modal closes via onSuccess
  };

  // Pre-select "Weather Briefing" on the YouTube kind once subcategories load.
  // Only pre-selects if the user hasn't picked something yet, so changing kind
  // mid-form doesn't clobber an explicit selection.
  useEffect(() => {
    if (!isYouTube || subcategoryId) return;
    const wb = subcategories.find((t) => t.name === YOUTUBE_DEFAULT_SUBCATEGORY);
    if (wb) setSubcategoryId(wb.id);
  }, [isYouTube, subcategories, subcategoryId]);

  const heading = isYouTube ? 'Add YouTube Video' : 'Add External Link';
  const urlPlaceholder = isYouTube
    ? 'https://www.youtube.com/watch?v=...'
    : 'https://...';
  const urlHelp = isYouTube
    ? 'Paste the YouTube URL — the CMS extracts the video ID and fetches title and thumbnail server-side.'
    : 'Paste the full URL editors should be sent to.';

  return (
    <div className="weather-url-form">
      <h3 className="multi-uploader__title">{heading}</h3>

      <div className="weather-url-form__field">
        <label htmlFor="wuf-url">URL <span className="weather-url-form__required">*</span></label>
        <input
          id="wuf-url"
          type="url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder={urlPlaceholder}
          autoFocus
        />
        <p className="weather-url-form__help">{urlHelp}</p>
      </div>

      <div className="weather-url-form__row">
        <div className="weather-url-form__field">
          <label htmlFor="wuf-date">Date <span className="weather-url-form__required">*</span></label>
          <input
            id="wuf-date"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
        </div>

        <div className="weather-url-form__field">
          <label htmlFor="wuf-subcategory">Subcategory <span className="weather-url-form__required">*</span></label>
          <select
            id="wuf-subcategory"
            value={subcategoryId}
            onChange={(e) => setSubcategoryId(e.target.value)}
          >
            <option value="">— select —</option>
            {subcategories.map((t) => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="weather-url-form__field">
        <label htmlFor="wuf-description">Description</label>
        <textarea
          id="wuf-description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={3}
          placeholder="Shown on the weather product card in EasyMap"
        />
      </div>

      {error && (
        <p className="weather-url-form__error" role="alert">{error}</p>
      )}

      <div className="multi-uploader__actions">
        <button
          type="button"
          className="multi-uploader__btn multi-uploader__btn--cancel"
          onClick={onCancel}
          disabled={submitting}
        >
          Cancel
        </button>
        <button
          type="button"
          className="multi-uploader__btn multi-uploader__btn--primary"
          onClick={handleSubmit}
          disabled={!canSubmit}
        >
          {submitting ? 'Adding…' : (isYouTube ? 'Add YouTube' : 'Add Link')}
        </button>
      </div>
    </div>
  );
};

export default WeatherUrlForm;
